/**
 * Export the workflow SVG to PNG via canvas.
 */
export function exportToPng(svgElement: SVGSVGElement, filename: string) {
  const svgData = new XMLSerializer().serializeToString(svgElement)
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(svgBlob)

  const img = new Image()
  img.onload = () => {
    const canvas = document.createElement('canvas')
    canvas.width = svgElement.clientWidth * 2  // 2x for retina
    canvas.height = svgElement.clientHeight * 2
    const ctx = canvas.getContext('2d')!
    ctx.scale(2, 2)
    ctx.drawImage(img, 0, 0)
    URL.revokeObjectURL(url)

    const pngUrl = canvas.toDataURL('image/png')
    const a = document.createElement('a')
    a.download = `${filename}.png`
    a.href = pngUrl
    a.click()
  }
  img.src = url
}

/**
 * Export the workflow SVG to PDF via jsPDF (loaded from window global or falls back to PNG).
 * The legacy app loaded jsPDF from a CDN script tag — we check for it at runtime.
 */
export function exportToPdf(svgElement: SVGSVGElement, filename: string) {
  // Check if jsPDF is available globally (loaded via script tag)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const jspdfGlobal = (window as any).jspdf as
    | { jsPDF: new (opts: Record<string, unknown>) => { addImage: (...args: unknown[]) => void; save: (name: string) => void } }
    | undefined

  if (!jspdfGlobal?.jsPDF) {
    // Fallback: just export as PNG with .pdf in the toast message
    exportToPng(svgElement, filename)
    return
  }

  const w = svgElement.clientWidth
  const h = svgElement.clientHeight
  const svgData = new XMLSerializer().serializeToString(svgElement)
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(svgBlob)

  const img = new Image()
  img.onload = () => {
    const canvas = document.createElement('canvas')
    canvas.width = w * 2
    canvas.height = h * 2
    const ctx = canvas.getContext('2d')!
    ctx.scale(2, 2)
    ctx.drawImage(img, 0, 0)
    URL.revokeObjectURL(url)

    const imgData = canvas.toDataURL('image/png')
    const pdf = new jspdfGlobal.jsPDF({
      orientation: w > h ? 'landscape' : 'portrait',
      unit: 'px',
      format: [w, h],
    })
    pdf.addImage(imgData, 'PNG', 0, 0, w, h)
    pdf.save(`${filename}.pdf`)
  }
  img.src = url
}

/**
 * Export XPDL content as a file download.
 */
export function downloadXpdl(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/xml' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.download = `${filename}.xpdl`
  a.href = url
  a.click()
  URL.revokeObjectURL(url)
}
